import React from 'react';
import Layout from '../components/Layout';
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { 
  ArrowRight, 
  GraduationCap, 
  Briefcase, 
  Globe, 
  MapPin,
  Quote,
  ExternalLink
} from 'lucide-react';

import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const stories = [
  {
    name: "Thomas",
    role: "Software Engineering Intern",
    school: "Engineering School, Lyon",
    company: "Sound & Vision Technologies (Care Cube)",
    location: "Osaka, Japan",
    duration: "6 months",
    img: thomasPortrait,
    imgAlt: "Thomas, engineering intern from Lyon",
    quote: "Through this internship, I've learned the importance of teamwork and how to solve problems collaboratively. I'm truly glad that I chose to come to Japan.",
    highlights: [
      "Built an iOS application from scratch for the company's main product",
      "Actively participated at tech exhibitions, presenting work to industry professionals",
      "Explored Japanese culture through visits to Kyoto, Fushimi Inari, and beyond"
    ],
    takeaway: "Living and working abroad helped me develop stronger self-management skills. Adapting to a new environment gave me greater confidence to take on new challenges.",
    link: "https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3",
    featured: true,
    galleryImages: [
      { src: thomasExhibition, alt: "Thomas at a tech exhibition" },
      { src: thomasFushimi, alt: "Fushimi Inari Taisha, Kyoto" }
    ]
  },
  {
    name: "Grégoire",
    role: "Engineering Exchange Student",
    school: "ICAM Strasbourg-Europe",
    company: "Shibaura Institute of Technology",
    location: "Tokyo, Japan",
    duration: "Academic semester",
    img: gregoireJapan,
    imgAlt: "Grégoire during his exchange in Japan",
    quote: "I chose Japan because its universities offer opportunities to explore cutting-edge science, robotics, and AI. I also wanted to step out of my comfort zone and immerse myself in a completely different culture.",
    highlights: [
      "Studied at Shibaura Institute of Technology, one of Japan's leading tech universities",
      "Explored robotics, artificial intelligence, and advanced engineering topics",
      "Engaged in student life: sports, cultural activities, and discovering Tokyo"
    ],
    takeaway: "If I could give advice to students interested in Japan: don't wait, just go for it! Learn Japanese to make the experience even richer and more memorable.",
    link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
  },
  {
    name: "Diego",
    role: "Data Analyst Intern",
    school: "UCLy / ESTRI Lyon",
    company: "MSC Japan (Mediterranean Shipping Company)",
    location: "Tokyo, Japan",
    duration: "4-6 months",
    img: diegoTokyo,
    imgAlt: "Diego during his internship in Tokyo",
    quote: "I was able to put my language and intercultural courses into practice in a real professional context. I came back more autonomous, more adaptable, and with a greatly expanded cultural openness.",
    highlights: [
      "Worked as a Data Analyst at MSC Japan, collecting and reporting data for regional managers",
      "Operated in a fully international, English-speaking work environment",
      "Combined professional immersion with deep cultural discovery of Japan"
    ],
    takeaway: "Professionally, it's a strong line on my CV. Personally, I came back more autonomous, more adaptable, and with a much broader cultural perspective.",
    link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
  },
  {
    name: "Yoann",
    role: "Embedded Systems Intern & Filmmaker",
    school: "CESI Toulouse",
    company: "Franco-Japanese IoT Startup",
    location: "Shibuya, Tokyo, Japan",
    duration: "3 months",
    img: yoannJapan,
    imgAlt: "Yoann during his internship in Japan",
    quote: "Traveling alone to the other side of the world is destabilizing, but these experiences are the ones that allow you to truly grow and discover yourself.",
    highlights: [
      "Developed a connected water quality sensor at an IoT startup in Shibuya",
      "Created 'Yugen', a feature-length documentary about Japan, premiered in Toulouse's largest cinema",
      "Assembled a team of 20 people for the film project, developing leadership and project management skills"
    ],
    takeaway: "Managing a team, handling a long and complex project, learning how to delegate — these are essential skills regardless of one's profession. This experience perfectly complements my engineering education.",
    link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
  },
  {
    name: "Mirabelle",
    role: "Chemical Engineering Intern",
    school: "CPE Lyon",
    company: "Air Liquide Laboratories",
    location: "Tsukuba, Japan",
    duration: "Gap year (12 months)",
    img: mirabelleJapan,
    imgAlt: "A pagoda in Japan, captured during Mirabelle's gap year",
    quote: "This experience abroad is the best choice I could have made! Going far away lets you gain perspective on your life and what you thought was universal.",
    highlights: [
      "Worked on chemical film deposits (ALD/CVD) for semiconductor manufacturing at Air Liquide",
      "Collaborated in a truly multicultural team: French, Spanish, Mexican, Korean, and Japanese colleagues",
      "Took Japanese language classes provided by the company, enhancing daily life integration"
    ],
    takeaway: "I've learned enormously — not only about my work, but also about things I wouldn't have been interested in on my own. Conversations with colleagues taught me about their cultures and ways of seeing life.",
    link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
  }
];

const FeaturedStory = ({ story }) => (
  <div data-testid="featured-story" className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden mb-16">
    <div className="grid grid-cols-1 lg:grid-cols-5">
      <div className="lg:col-span-2 relative h-72 lg:h-auto min-h-[320px]">
        <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent lg:bg-gradient-to-r"></div>
        <div className="absolute bottom-6 left-6">
          <Badge className="bg-red-600 text-white border-none mb-2">Featured</Badge>
          <p className="text-white font-bold text-2xl">{story.name}</p>
          <p className="text-white/80 text-sm">{story.role}</p>
        </div>
      </div>
      <div className="lg:col-span-3 p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <GraduationCap className="h-4 w-4" /> {story.school}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <Briefcase className="h-4 w-4" /> {story.company}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <MapPin className="h-4 w-4" /> {story.location} &middot; {story.duration}
        </div>

        <blockquote className="mt-6 text-lg text-slate-700 italic leading-relaxed border-l-4 border-red-500 pl-4 mb-6">
          "{story.quote}"
        </blockquote>

        <h4 className="font-bold text-slate-900 mb-3">Key Highlights</h4>
        <ul className="space-y-2 mb-6">
          {story.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <ArrowRight className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              {h}
            </li>
          ))}
        </ul>

        {story.galleryImages && (
          <div className="flex gap-3 mb-6">
            {story.galleryImages.map((img, i) => (
              <div key={i} className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        <a
          href={story.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="featured-story-link"
          className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
        >
          Read Full Story on Medium
          <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </div>
  </div>
);

const StoryCard = ({ story }) => (
  <Card data-testid={`story-card-${story.name.toLowerCase()}`} className="overflow-hidden border-slate-100 hover:shadow-xl hover:border-red-200 transition-all duration-300 flex flex-col">
    <div className="relative h-52 overflow-hidden">
      <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
      <div className="absolute bottom-3 left-4">
        <p className="text-white font-bold text-xl">{story.name}</p>
        <p className="text-white/80 text-xs">{story.school}</p>
      </div>
    </div>
    <CardContent className="p-6 flex flex-col flex-1">
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="font-medium">{story.role}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          {story.company}, {story.location}
        </div>
        <p className="text-xs text-slate-400">{story.duration}</p>
      </div>

      <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3 mb-4 flex-1">
        "{story.quote}"
      </blockquote>

      <h4 className="font-semibold text-slate-900 text-sm mb-2">Highlights</h4>
      <ul className="space-y-1.5 mb-5">
        {story.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
            <ArrowRight className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
            {h}
          </li>
        ))}
      </ul>

      <p className="text-xs text-slate-500 italic mb-4 bg-slate-50 rounded-lg p-3">
        <Quote className="h-3 w-3 text-red-400 inline mr-1" />
        {story.takeaway}
      </p>

      <a
        href={story.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-red-600 text-sm font-semibold hover:text-red-700 transition-colors group mt-auto"
      >
        Read Full Story
        <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>
    </CardContent>
  </Card>
);

const Testimonials = () => {
  const featured = stories.find(s => s.featured);
  const others = stories.filter(s => !s.featured);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-20 pb-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">Testimonials</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Real Stories from Real Interns</h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            French students share their experiences working and living in Japan — from engineering labs in Tsukuba to IoT startups in Shibuya.
          </p>
        </div>
      </section>

      {/* Featured Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <FeaturedStory story={featured} />

          {/* Other Stories Grid */}
          <h2 className="text-2xl font-bold text-slate-900 mb-8">More Experiences</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {others.map((story, i) => (
              <StoryCard key={i} story={story} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-2xl">
          <h2 className="text-3xl font-bold mb-4">Your Story Could Be Next</h2>
          <p className="text-slate-300 mb-8">
            Whether you're a company looking for exceptional international talent or a student dreaming of Japan — Internjin makes it happen.
          </p>
          <a 
            href="mailto:contact@internjin.com"
            data-testid="testimonials-cta"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-full transition-colors"
          >
            Get in Touch <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default Testimonials;
